import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Search, Sparkles, BookOpen, ExternalLink, Loader2, 
  FileText, AlertCircle, CheckCircle, Info, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OSHARegulationsSearch = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Enter a query",
        description: "Please type your OSHA regulation question",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    setSearchResults(null);

    try {
      const response = await axios.post(`${API}/osha/regulations-search`, {
        query: searchQuery
      });

      setSearchResults(response.data);
      
      // Add to history
      setSearchHistory(prev => [
        {
          query: searchQuery,
          timestamp: new Date().toISOString(),
          id: Date.now()
        },
        ...prev.slice(0, 9) // Keep last 10 searches
      ]);

      toast({
        title: "Search Complete",
        description: "Found relevant OSHA regulations"
      });
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search Failed",
        description: error.response?.data?.detail || "Unable to search regulations",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const quickSearches = [
    "Fall protection requirements for construction",
    "Lockout/tagout procedures",
    "Personal protective equipment standards",
    "Hazard communication requirements",
    "Confined space entry regulations",
    "Respiratory protection standards",
    "Electrical safety requirements",
    "Machine guarding regulations"
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header with Background Image */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-xl mb-6">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1590402494682-cd3fb53b1f70?auto=format&fit=crop&w=1200&q=80" 
            alt="Worker reviewing safety regulations"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative px-8 py-12 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-3">OSHA Regulations Search</h1>
          <p className="text-xl text-blue-100">AI-powered search for OSHA standards and regulations</p>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6" />
            <h2 className="text-xl font-semibold">AI-Powered Regulation Search</h2>
          </div>
          <p className="text-blue-100 mb-4">
            Ask any question about OSHA regulations and get instant, comprehensive answers with official references
          </p>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                placeholder="e.g., What are the fall protection requirements for scaffolding over 10 feet?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="bg-white text-gray-900 pr-10 h-12 text-base"
              />
              <Search className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
            </div>
            <Button 
              onClick={handleSearch}
              disabled={isSearching}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-8"
            >
              {isSearching ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Searches */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Info className="w-5 h-5 mr-2 text-blue-600" />
            Popular Searches
          </CardTitle>
          <CardDescription>Click any topic to search instantly</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {quickSearches.map((query, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setSearchQuery(query);
                  setTimeout(() => handleSearch(), 100);
                }}
                className="text-left p-3 rounded-lg border hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{query}</span>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-900">
              <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
              Search Results
            </CardTitle>
            <CardDescription className="text-blue-700">
              AI-generated summary with official OSHA references
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Main Answer */}
            <div className="bg-white rounded-lg p-6 border border-blue-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Answer</h3>
                  <p className="text-sm text-gray-600 mb-2">Your question: <span className="font-medium">{searchResults.query}</span></p>
                </div>
              </div>
              <div className="prose max-w-none">
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {searchResults.answer}
                </p>
              </div>
            </div>

            {/* Relevant Standards */}
            {searchResults.relevantStandards && searchResults.relevantStandards.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                  Relevant OSHA Standards
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {searchResults.relevantStandards.map((standard, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="font-mono">
                              {standard.number}
                            </Badge>
                            <h4 className="font-semibold text-gray-900">{standard.title}</h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{standard.description}</p>
                          {standard.url && (
                            <a
                              href={standard.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              View on OSHA.gov
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Requirements */}
            {searchResults.keyRequirements && searchResults.keyRequirements.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
                  Key Requirements
                </h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <ul className="space-y-2">
                    {searchResults.keyRequirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Related Topics */}
            {searchResults.relatedTopics && searchResults.relatedTopics.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Related Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {searchResults.relatedTopics.map((topic, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary" 
                      className="cursor-pointer hover:bg-blue-100"
                      onClick={() => {
                        setSearchQuery(topic);
                        setTimeout(() => handleSearch(), 100);
                      }}
                    >
                      {topic}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Searches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {searchHistory.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setSearchQuery(item.query);
                  }}
                  className="w-full text-left p-3 rounded-lg border hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{item.query}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information Card */}
      <Card className="bg-gray-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">About This Search</h3>
              <p className="text-sm text-gray-600 mb-3">
                This AI-powered search tool provides comprehensive information about OSHA regulations, standards, 
                and compliance requirements. Results are generated using advanced AI and reference official OSHA documentation.
              </p>
              <p className="text-xs text-gray-500">
                <strong>Note:</strong> While this tool provides accurate information, always verify critical compliance 
                requirements with official OSHA resources and consult with safety professionals for specific situations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OSHARegulationsSearch;
